import { all, call, delay, fork, put, select, takeEvery } from "redux-saga/effects";
import { LOCATION_CHANGE as Connected_React_Router_Route_Change, LocationChangeAction, push } from "connected-react-router";
import { LocationState, Location } from 'history'
import { STASH_LOCATION_SAVE } from "@/reducers/stashLocation";
import authProvider from "@/authProvider/authProvider"
import { CallReturnType } from "@/utils/sagaTypsHelper";
import { SECUREFILE_ONLY } from "@/constants";
import { BreadcrumbItemProvider } from "@/types/resource";
import { getBreadcrumbProviders } from "@/reducers/breadcrumbProvider";
import { raiseResetBreadcrumbsProviders } from "@/actions/breadcrumbProvider";
import { CastedUserManager, AccessTokenClaims } from "@/authProvider/odUserManager";
import { UserResponseDto } from "@/gen/api/user";
import { ensureUserProfile } from "./ensureUserProfile";


type RState = Location<LocationState>
const traceLength = 4
const cache : RState[] = []

const changeHandlers : ((from: RState|undefined, to: RState) => Generator)[] = []

export function* handleTemporalRouteTrace(input: LocationChangeAction) {
  const { payload: { location, action } } = input
  
  const currLocation = cache[0]

  if (location.pathname === currLocation?.pathname) { return }

  cache.unshift(location)
  if (cache.length > traceLength) { cache.length = traceLength }
  yield all(changeHandlers.map(handler => call(handler, currLocation, location)))
}
  

const dbRoutes = {
  detail: /^\/databases\/[^/]+\/show/,
  backup: /^\/backups-n-validations\/list\/[^/]+\/show/,
  encrypt: /^\/encryption\/list\/[^/]+\/show/,
  deploy: /^\/deployments\/list\/[^/]+\/show/,
  keyPolicy: /^\/databases\/[^/]+\/keys-policy/,
  dbESC: /^\/databases\/[^/]+\/encryption-setting-change/,
}
const watchToDBDetail = function* (from: RState|undefined, to: RState) {
  if (dbRoutes.detail.test(to.pathname)) {
    const arr = Object.values(dbRoutes)

    const prevLocation = cache.find(l => arr.every(r => !r.test(l.pathname)))
    if (prevLocation) {
      yield put({ type: STASH_LOCATION_SAVE, payload: prevLocation })
    }
  }
}

const watchToDocs = function* (from: RState|undefined, to: RState) {
  if (to.pathname.startsWith('/documents')) {
    if (from && !from.pathname.startsWith('/documents')) {
      yield delay(300)
      yield put({ type: STASH_LOCATION_SAVE, payload: from })
    }
  }
}

let cachedPermissions = undefined as { permissions: (readonly string[]); isSecureFileOnly: boolean}|undefined
authProvider.userManager.events.addUserLoaded(() => {
  // clear previously cached permission whenever new user session started
  cachedPermissions = undefined
})
authProvider.userManager.events.addUserUnloaded(() => {
  // clear previously cached permission whenever new user session ended
  cachedPermissions = undefined
})
const restrictSecureFileOnly = function* (from: RState|undefined, to: RState) {
  if (!cachedPermissions) {
    const getPermissions: (params: any) => Promise<ReadonlyArray<string>> = authProvider.getPermissions.bind(authProvider)
    const permissions: CallReturnType<typeof getPermissions> = yield call<typeof getPermissions>(getPermissions, {})
    cachedPermissions = {
      permissions,
      // if haven't login, permissions == [], isSecureFileOnly evals to false
      isSecureFileOnly: (permissions ?? []).indexOf(SECUREFILE_ONLY) > -1,
    }
  }
  if (!cachedPermissions.isSecureFileOnly) { return }

  // only user profile and documentation pages are allowed for SecureFile_Only users
  if (to.pathname.startsWith('/profile') || to.pathname.startsWith('/documents')
   || to.pathname.startsWith('/reset-password')
   || to.pathname.startsWith('/change-password')
  ) { return }

  yield put(push('/profile/edit'))
  window.location.assign('/profile/edit')
}

const restrictLocalUserWithNoPhone = function* (from: RState|undefined, to: RState) {
  const getUserFn = authProvider.userManager.getUser.bind(authProvider.userManager)
  const user: CallReturnType<typeof getUserFn> = yield call(getUserFn)
  // don't proceed if user haven't log in
  if (!user || user.expired) { return }

  let isLocalLogin = true
  try {
    const jwtAt = (authProvider.userManager as CastedUserManager)._joseUtil.parseJwt<AccessTokenClaims>(user.access_token || '')
    isLocalLogin = jwtAt.payload.idp === 'local'
  } catch {
    isLocalLogin = true
  }
  
  if (!isLocalLogin) { return }
  const {ensureUserProfileSaga, userProfileDeferred} = ensureUserProfile()
  yield fork(ensureUserProfileSaga)
  const userProfile: UserResponseDto|undefined = yield userProfileDeferred
  
  if (userProfile && userProfile.phone) { return }

  // only user profile and documentation pages are allowed, similar to SecureFile_Only users
  if (to.pathname.startsWith('/profile/create') || to.pathname.startsWith('/documents')
   || to.pathname.startsWith('/reset-password')
   || to.pathname.startsWith('/change-password')
   || to.pathname.startsWith('/device-consent')
  ) { return }
  
  yield put(push('/profile/create'))
  window.location.assign('/profile/create')
}

const maintainBreadcrumbs = function* (from: RState|undefined, to: RState) {
  const bc_storage_key = 'od_extra_bc'
  const bcProviders: BreadcrumbItemProvider[] = yield select(getBreadcrumbProviders)
  if (!bcProviders || bcProviders.length === 0) { 
    // window.sessionStorage.removeItem(bc_storage_key)
    return 
  }
  const tail = bcProviders[bcProviders.length - 1]
  // if (from?.pathname !== tail.pathname) {
  //   yield put(raiseResetBreadcrumbsProviders())
  //   return
  // }
  const satisfies = (term: string|((to: RState)=>boolean)): boolean => {
    if (typeof term === 'string') {
      return term === '*' || to.pathname.startsWith(term)
    } else {
      return term(to)
    }
  }

  if (
    (tail.toExclude && satisfies(tail.toExclude)) ||
    !satisfies(tail.toMatch)
  ) {
    yield put(raiseResetBreadcrumbsProviders())
    window.sessionStorage.removeItem(bc_storage_key)
    return
  }
  if (typeof tail.item === 'function') {
    tail.item = tail.item(to)
  }
  window.sessionStorage.setItem(bc_storage_key, JSON.stringify(bcProviders))
}

changeHandlers.push(
  watchToDBDetail,
  watchToDocs,
  restrictSecureFileOnly,
  restrictLocalUserWithNoPhone,
  maintainBreadcrumbs
)

export default function* temporalRouteTraceSaga() {
  yield all([
    takeEvery(Connected_React_Router_Route_Change, handleTemporalRouteTrace),
  ])
}

